import { createContext, useContext, useState, useEffect } from "react";

export const translations = {
  en: {
    nav: { services: "Services", insurance: "Insurance", fleet: "Fleet & B2B", areas: "Service Area", quote: "Instant Quote" },
    hero: {
      eyebrow: "Auto Glass · Greater Boston",
      lines: ["A stone hits.", "Glass cracks.", "We make it disappear."],
      sub: "Premium windshield repair & replacement from our Peabody shop — with mobile service across Greater Boston. Most insurance claims at $0 deductible.",
      ctaPrimary: "Get my instant quote",
      ctaSecondary: "WhatsApp us",
      note: "Trilingual team · English / Português / Español",
    },
    bar: { address: "98 Foster St, Peabody, MA", hours: "Mon–Fri 8–7 · Sat 10–4", phone: "+1 857-615-2967" },
    services: {
      label: "What we do",
      title: "Five ways we put you back on the road.",
      items: [
        { title: "Chip & Crack Repair", desc: "A quarter-sized chip today is a full replacement tomorrow. We seal it in 30 minutes — often free with insurance." },
        { title: "Windshield Replacement", desc: "OEM-quality glass, factory-grade urethane, safe drive-away times. Done right the first time, guaranteed." },
        { title: "ADAS Calibration", desc: "Modern windshields carry cameras and sensors. We recalibrate lane-keep, braking and cruise systems to spec." },
        { title: "Mobile Service", desc: "Home, office or curbside — our fully equipped vans come to you anywhere in Greater Boston, same day in most cases." },
        { title: "Side & Rear Glass", desc: "Door glass, quarter glass, back glass — replaced and vacuumed clean, with new molding and defroster hookups." },
      ],
    },
    insurance: {
      label: "Insurance, handled",
      title: "$0 deductible.",
      title2: "Zero paperwork.",
      sub: "In Massachusetts, most comprehensive plans cover auto glass with no deductible. We bill your insurer directly and manage the claim from first call to final sign-off.",
      points: ["All major insurers accepted", "We file the claim for you", "No upfront cost in most cases"],
      cta: "Start my claim",
    },
    quote: {
      label: "Instant quote",
      title: "Tell us about the damage.",
      sub: "Plate or VIN + a photo of the damage. We reply with a firm price — usually within 15 minutes during business hours.",
      name: "Full name *",
      phone: "Phone / WhatsApp *",
      email: "E-mail (optional)",
      plate: "License plate or VIN",
      vehicle: "Vehicle — year / make / model",
      service: "Service needed",
      message: "Describe the damage (optional)",
      photo: "Drop a photo of the damage here, or tap to upload",
      photoReady: "Photo attached — tap to replace",
      options: {
        repair: "Chip / crack repair",
        replacement: "Windshield replacement",
        sideglass: "Side or rear glass",
        adas: "ADAS calibration",
        fleet: "Fleet / B2B service",
        other: "Not sure — advise me",
      },
      submit: "Send my quote request",
      sending: "Sending…",
      successTitle: "Request received!",
      successBody: "We'll reach out shortly with your quote.",
      error: "Couldn't send right now — please try WhatsApp.",
      whyTitle: "Why drivers pick ClearRoute",
      why: [
        { t: "Replies in ~15 min", d: "Real humans, real prices, during business hours." },
        { t: "$0 deductible", d: "We work directly with your insurance." },
        { t: "We come to you", d: "Mobile units across Greater Boston." },
      ],
    },
    fleet: {
      label: "Fleet & B2B",
      title: "Keep every vehicle earning.",
      sub: "Dealers, body shops, rideshare and delivery fleets across Greater Boston run on ClearRoute. One call, one invoice, zero downtime.",
      bullets: ["Volume & dealer pricing", "Priority same-day slots", "Mobile service at your lot", "Monthly consolidated billing"],
      cta: "Talk to our fleet team",
    },
    mobile: {
      label: "Mobile service",
      title: "Our shop on wheels comes to you.",
      sub: "Fully equipped ClearRoute vans cover Greater Boston — home, office or curbside. Same certified techs, same OEM-quality glass, same lifetime warranty as in the shop.",
      bullets: ["Same-day slots in most cities", "Windshield, ADAS calibration & more, done on-site", "No extra fee within our service area"],
      cta: "Book a mobile visit",
    },
    areas: {
      label: "Service area",
      title: "Greater Boston, covered.",
      sub: "Shop in Peabody. Mobile units across the North Shore, Metro Boston and beyond.",
      note: "Don't see your city? We probably still come to you — ask us on WhatsApp.",
    },
    cities: ["Peabody", "Boston", "Cambridge", "Somerville", "Lynn", "Salem", "Beverly", "Danvers", "Saugus", "Revere", "Malden", "Medford", "Everett", "Stoneham", "Woburn", "Lynnfield", "Newton", "Brookline", "Quincy", "Chelsea"],
    footer: {
      tagline: "See Clearly. Drive Safely.",
      contact: "Contact",
      hours: "Hours",
      weekday: "Monday – Friday",
      sat: "Saturday",
      sun: "Sunday — closed",
      langNote: "Proudly serving in English, Português y Español.",
      admin: "Team login",
      rights: "All rights reserved.",
    },
    whatsapp: { tooltip: "Chat on WhatsApp", prefill: "Hi ClearRoute! I need a quote for my auto glass." },
  },
  pt: {
    nav: { services: "Serviços", insurance: "Seguro", fleet: "Frotas & B2B", areas: "Área de atendimento", quote: "Orçamento instantâneo" },
    hero: {
      eyebrow: "Vidros automotivos · Greater Boston",
      lines: ["Uma pedra bate.", "O vidro trinca.", "Nós fazemos sumir."],
      sub: "Reparo e troca de para-brisa premium na nossa loja em Peabody — com serviço móvel em toda Greater Boston. A maioria dos seguros com franquia $0.",
      ctaPrimary: "Quero meu orçamento",
      ctaSecondary: "Chamar no WhatsApp",
      note: "Equipe trilíngue · English / Português / Español",
    },
    bar: { address: "98 Foster St, Peabody, MA", hours: "Seg–Sex 8–19 · Sáb 10–16", phone: "+1 857-615-2967" },
    services: {
      label: "O que fazemos",
      title: "Cinco jeitos de te colocar de volta na estrada.",
      items: [
        { title: "Reparo de trincas e lascas", desc: "Uma lasca pequena hoje vira uma troca completa amanhã. Selamos em 30 minutos — muitas vezes sem custo pelo seguro." },
        { title: "Troca de para-brisa", desc: "Vidro qualidade OEM, uretano de nível de fábrica e tempo seguro de cura. Feito certo na primeira, com garantia." },
        { title: "Calibração ADAS", desc: "Para-brisas modernos carregam câmeras e sensores. Recalibramos sistemas de faixa, frenagem e piloto automático." },
        { title: "Serviço móvel", desc: "Casa, trabalho ou na rua — nossas vans equipadas vão até você em Greater Boston, na maioria dos casos no mesmo dia." },
        { title: "Vidros laterais e traseiros", desc: "Vidro de porta, vigia e vigia traseira — trocados e aspirados, com molduras novas e desembaçador ligado." },
      ],
    },
    insurance: {
      label: "Seguro sem dor de cabeça",
      title: "Franquia $0.",
      title2: "Zero burocracia.",
      sub: "Em Massachusetts, a maioria dos planos cobre vidros sem franquia. Cobramos direto da seguradora e cuidamos do processo do início ao fim.",
      points: ["Todas as grandes seguradoras", "Nós abrimos o sinistro por você", "Sem custo inicial na maioria dos casos"],
      cta: "Iniciar meu sinistro",
    },
    quote: {
      label: "Orçamento instantâneo",
      title: "Conte pra gente o que aconteceu.",
      sub: "Placa ou VIN + uma foto do dano. Respondemos com preço fechado — normalmente em 15 minutos no horário comercial.",
      name: "Nome completo *",
      phone: "Telefone / WhatsApp *",
      email: "E-mail (opcional)",
      plate: "Placa ou VIN",
      vehicle: "Veículo — ano / marca / modelo",
      service: "Serviço desejado",
      message: "Descreva o dano (opcional)",
      photo: "Solte uma foto do dano aqui, ou toque para enviar",
      photoReady: "Foto anexada — toque para trocar",
      options: {
        repair: "Reparo de trinca / lasca",
        replacement: "Troca de para-brisa",
        sideglass: "Vidro lateral ou traseiro",
        adas: "Calibração ADAS",
        fleet: "Frota / B2B",
        other: "Não sei — me orientem",
      },
      submit: "Enviar pedido de orçamento",
      sending: "Enviando…",
      successTitle: "Pedido recebido!",
      successBody: "Em breve entraremos em contato com seu orçamento.",
      error: "Não foi possível enviar agora — tente pelo WhatsApp.",
      whyTitle: "Por que escolher a ClearRoute",
      why: [
        { t: "Resposta em ~15 min", d: "Gente de verdade, preço de verdade, no horário comercial." },
        { t: "Franquia $0", d: "Trabalhamos direto com seu seguro." },
        { t: "Vamos até você", d: "Unidades móveis em toda Greater Boston." },
      ],
    },
    fleet: {
      label: "Frotas & B2B",
      title: "Nenhum veículo parado.",
      sub: "Concessionárias, oficinas, motoristas de app e frotas de entrega em Greater Boston rodam com a ClearRoute. Uma ligação, uma fatura, zero tempo parado.",
      bullets: ["Preços de volume e dealer", "Agenda prioritária no mesmo dia", "Serviço móvel no seu pátio", "Faturamento mensal consolidado"],
      cta: "Falar com o time de frotas",
    },
    mobile: {
      label: "Serviço móvel",
      title: "Nossa oficina sobre rodas vai até você.",
      sub: "Vans ClearRoute totalmente equipadas cobrem toda Greater Boston — em casa, no trabalho ou na rua. Os mesmos técnicos certificados, o mesmo vidro qualidade OEM e a mesma garantia da loja.",
      bullets: ["Horários no mesmo dia na maioria das cidades", "Para-brisa, calibração ADAS e muito mais, no local", "Sem taxa extra dentro da área de atendimento"],
      cta: "Agendar visita móvel",
    },
    areas: {
      label: "Área de atendimento",
      title: "Greater Boston inteira, coberta.",
      sub: "Loja em Peabody. Unidades móveis por toda North Shore, região metropolitana de Boston e além.",
      note: "Não achou sua cidade? Provavelmente atendemos mesmo assim — chame no WhatsApp.",
    },
    cities: ["Peabody", "Boston", "Cambridge", "Somerville", "Lynn", "Salem", "Beverly", "Danvers", "Saugus", "Revere", "Malden", "Medford", "Everett", "Stoneham", "Woburn", "Lynnfield", "Newton", "Brookline", "Quincy", "Chelsea"],
    footer: {
      tagline: "Veja claro. Dirija seguro.",
      contact: "Contato",
      hours: "Horários",
      weekday: "Segunda – Sexta",
      sat: "Sábado",
      sun: "Domingo — fechado",
      langNote: "Atendemos com orgulho em English, Português y Español.",
      admin: "Acesso da equipe",
      rights: "Todos os direitos reservados.",
    },
    whatsapp: { tooltip: "Conversar no WhatsApp", prefill: "Oi ClearRoute! Preciso de um orçamento para o vidro do meu carro." },
  },
  es: {
    nav: { services: "Servicios", insurance: "Seguro", fleet: "Flotas & B2B", areas: "Área de servicio", quote: "Cotización instantánea" },
    hero: {
      eyebrow: "Vidrios de auto · Greater Boston",
      lines: ["Una piedra golpea.", "El vidrio se agrieta.", "Lo hacemos desaparecer."],
      sub: "Reparación y reemplazo de parabrisas premium en nuestra tienda de Peabody — con servicio móvil en todo Greater Boston. La mayoría de los seguros con deducible de $0.",
      ctaPrimary: "Quiero mi cotización",
      ctaSecondary: "Escríbenos por WhatsApp",
      note: "Equipo trilingüe · English / Português / Español",
    },
    bar: { address: "98 Foster St, Peabody, MA", hours: "Lun–Vie 8–7 · Sáb 10–4", phone: "+1 857-615-2967" },
    services: {
      label: "Lo que hacemos",
      title: "Cinco formas de devolverte a la carretera.",
      items: [
        { title: "Reparación de astillas y grietas", desc: "Una astilla pequeña hoy es un reemplazo completo mañana. La sellamos en 30 minutos — muchas veces gratis con seguro." },
        { title: "Reemplazo de parabrisas", desc: "Vidrio calidad OEM, uretano de grado de fábrica y tiempos seguros de secado. Bien hecho a la primera, garantizado." },
        { title: "Calibración ADAS", desc: "Los parabrisas modernos llevan cámaras y sensores. Recalibramos los sistemas de carril, frenado y crucero." },
        { title: "Servicio móvil", desc: "Casa, oficina o en la calle — nuestras vans equipadas van a ti en Greater Boston, casi siempre el mismo día." },
        { title: "Vidrios laterales y traseros", desc: "Vidrio de puerta, custodia y medallón — reemplazados y aspirados, con molduras nuevas y desempañador conectado." },
      ],
    },
    insurance: {
      label: "Seguro sin estrés",
      title: "Deducible $0.",
      title2: "Cero papeleo.",
      sub: "En Massachusetts, la mayoría de los planes cubren vidrios sin deducible. Facturamos directo a tu aseguradora y manejamos el reclamo de principio a fin.",
      points: ["Aceptamos todas las aseguradoras", "Presentamos el reclamo por ti", "Sin costo inicial en la mayoría de los casos"],
      cta: "Iniciar mi reclamo",
    },
    quote: {
      label: "Cotización instantánea",
      title: "Cuéntanos sobre el daño.",
      sub: "Placa o VIN + una foto del daño. Respondemos con precio firme — normalmente en 15 minutos en horario laboral.",
      name: "Nombre completo *",
      phone: "Teléfono / WhatsApp *",
      email: "Correo (opcional)",
      plate: "Placa o VIN",
      vehicle: "Vehículo — año / marca / modelo",
      service: "Servicio necesario",
      message: "Describe el daño (opcional)",
      photo: "Suelta una foto del daño aquí, o toca para subir",
      photoReady: "Foto adjunta — toca para cambiar",
      options: {
        repair: "Reparación de grieta / astilla",
        replacement: "Reemplazo de parabrisas",
        sideglass: "Vidrio lateral o trasero",
        adas: "Calibración ADAS",
        fleet: "Flota / B2B",
        other: "No sé — aconséjenme",
      },
      submit: "Enviar mi solicitud",
      sending: "Enviando…",
      successTitle: "¡Solicitud recibida!",
      successBody: "Pronto te contactaremos con tu cotización.",
      error: "No se pudo enviar ahora — intenta por WhatsApp.",
      whyTitle: "Por qué elegir ClearRoute",
      why: [
        { t: "Respuesta en ~15 min", d: "Personas reales, precios reales, en horario laboral." },
        { t: "Deducible $0", d: "Trabajamos directo con tu seguro." },
        { t: "Vamos a ti", d: "Unidades móviles en todo Greater Boston." },
      ],
    },
    fleet: {
      label: "Flotas & B2B",
      title: "Ningún vehículo detenido.",
      sub: "Concesionarios, talleres, conductores de apps y flotas de entrega en Greater Boston confían en ClearRoute. Una llamada, una factura, cero tiempo perdido.",
      bullets: ["Precios de volumen y dealer", "Citas prioritarias el mismo día", "Servicio móvil en tu lote", "Facturación mensual consolidada"],
      cta: "Hablar con el equipo de flotas",
    },
    mobile: {
      label: "Servicio móvil",
      title: "Nuestro taller sobre ruedas va hasta ti.",
      sub: "Vans ClearRoute totalmente equipadas cubren todo Greater Boston — en tu casa, tu trabajo o en la calle. Los mismos técnicos certificados, el mismo vidrio calidad OEM y la misma garantía del taller.",
      bullets: ["Citas el mismo día en la mayoría de las ciudades", "Parabrisas, calibración ADAS y más, en el lugar", "Sin cargo extra dentro del área de servicio"],
      cta: "Agendar visita móvil",
    },
    areas: {
      label: "Área de servicio",
      title: "Todo Greater Boston, cubierto.",
      sub: "Tienda en Peabody. Unidades móviles por North Shore, el área metro de Boston y más allá.",
      note: "¿No ves tu ciudad? Seguro igual vamos — pregúntanos por WhatsApp.",
    },
    cities: ["Peabody", "Boston", "Cambridge", "Somerville", "Lynn", "Salem", "Beverly", "Danvers", "Saugus", "Revere", "Malden", "Medford", "Everett", "Stoneham", "Woburn", "Lynnfield", "Newton", "Brookline", "Quincy", "Chelsea"],
    footer: {
      tagline: "Ve claro. Maneja seguro.",
      contact: "Contacto",
      hours: "Horarios",
      weekday: "Lunes – Viernes",
      sat: "Sábado",
      sun: "Domingo — cerrado",
      langNote: "Orgullosamente atendemos en English, Português y Español.",
      admin: "Acceso del equipo",
      rights: "Todos los derechos reservados.",
    },
    whatsapp: { tooltip: "Chatear por WhatsApp", prefill: "¡Hola ClearRoute! Necesito una cotización para el vidrio de mi auto." },
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("cr-lang") || "en");
  const [flash, setFlash] = useState(false);
  const setLang = (l) => {
    if (l !== lang) {
      setFlash(true);
      setLangState(l);
      setTimeout(() => setFlash(false), 780);
    }
  };
  useEffect(() => {
    localStorage.setItem("cr-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const t = translations[lang] || translations.en;
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {flash && <div className="lang-flash" aria-hidden />}
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
